import { LockOutlined } from '@ant-design/icons'
import { Button, Modal } from 'antd'
import React, { FunctionComponent, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { FeatherIcon } from '@/components/custom-icon'
import useUnlockKolSnapshot from '@/components/kol/detail/hooks/use-unlock-kol-snapshot'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import useIrmContentContext from '@/components/layout/hooks/use-irm-content-context'
import { VipOnlyModal } from '@/components/modal/vip-only-modal'
import useIsJapan from '@/hooks/use-is-japan'
import { usePermissions } from '@/hooks/use-permissions'
import { useIntl } from '@/i18n/hooks/use-intl'
import { reduxAPI } from '@/store/redux-api'
import { ampli } from '@/utils/ampli'

interface KolExportButtonProps {
  kolId: string
  kolDcIdHashed?: string
  onClick: VoidFunction
  isDisabled: boolean
}

const KolExportButton: FunctionComponent<KolExportButtonProps> = ({
  kolId,
  kolDcIdHashed,
  onClick,
  isDisabled,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const irmContentContext = useIrmContentContext()

  const contactModal =
    reduxAPI.state.modal.hooks.useVisible('purchaseEnterprise')
  const isJapan = useIsJapan()
  const { withEnterprisePermission, withInternalPermission } = usePermissions()

  const [vipModalOpen, setVipModalOpen] = useState(false)

  const { isLocked, kolInfo } = useKolInfo(kolId, kolDcIdHashed)
  const { handleUnlock } = useUnlockKolSnapshot((): void => {
    setVipModalOpen(true)
  })

  const handleClick = (): void => {
    if (isJapan && !withEnterprisePermission && !withInternalPermission) {
      contactModal.toggleVisible()
      ampli.tryToExportKolData({
        kolId: kolInfo?.id,
        kolName: kolInfo?.name,
        isUnlocked: false,
      })
      return
    }

    if (isLocked) {
      ampli.tryToExportKolData({
        kolId: kolInfo?.id,
        kolName: kolInfo?.name,
        isUnlocked: false,
      })

      Modal.confirm({
        centered: true,
        width: 500,
        getContainer: irmContentContext?.containerRef?.current ?? document.body,
        title: (
          <Title>
            {formatMessage({ id: 'kol:export_locked_modal_title' })}
          </Title>
        ),
        content: (
          <Content>
            {formatMessage({ id: 'kol:export_locked_modal_content' })}
          </Content>
        ),
        okButtonProps: {
          icon: <LockOutlined />,
          style: {
            background: theme.colors.plan.premium,
            border: theme.colors.plan.premium,
          },
          size: 'large',
        },
        okText: formatMessage({ id: 'kol:export_locked_modal_ok_text' }),
        onOk: async () => {
          if (!kolInfo) {
            return
          }

          const unlocked = await handleUnlock(kolInfo)

          if (!unlocked) {
            return
          }

          onClick()
        },
        cancelButtonProps: {
          size: 'large',
        },
        cancelText: formatMessage({ id: 'general:btn_text_cancel' }),
      })
      return
    }

    ampli.tryToExportKolData({
      kolId: kolInfo?.id,
      kolName: kolInfo?.name,
      isUnlocked: true,
    })

    onClick()
  }

  return (
    <div>
      <Button
        disabled={isDisabled}
        icon={<FeatherIcon type='feather-download' />}
        onClick={handleClick}
      />
      <VipOnlyModal
        canContinueBrowsingFromProp
        onlyQuota
        source='Unlock KD Data'
        title={formatMessage(
          {
            id: 'kol:description_kol_info_quota_not_enough',
          },
          { br: <br /> },
        )}
        visible={vipModalOpen}
        onClose={(): void => {
          setVipModalOpen(false)
        }}
      />
    </div>
  )
}

const Title = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`

const Content = styled(Title)`
  font-weight: 400;
`

export default KolExportButton
